/**
 * User Zod schemalar
 *
 * Kanonik qarorlar:
 *   - 3 ta rol: EGASI, ADMIN, XODIM
 *   - Default rol: XODIM (eng past darajadagi rol)
 *   - Per-user permissions XODIM uchun moslashuvchan
 */
import { z } from 'zod';
import { emailSchema, loginPasswordSchema, loginUsernameSchema, passwordSchema, phoneSchema, usernameSchema, uuidSchema, } from './common.schema.js';
// ─── Enums ────────────────────────────────────────────────────────
export const userRoleEnum = z.enum(['EGASI', 'ADMIN', 'XODIM']);
export const subscriptionTypeEnum = z.enum(['TRIAL', 'MONTHLY', 'YEARLY', 'LIFETIME']);
// ─── Per-user permissions (spec 2.5: granular expand) ────────────
export const userPermissionsSchema = z
    .object({
    canSell: z.boolean().default(true),
    canEditProducts: z.boolean().default(false),
    canDeleteProducts: z.boolean().default(false),
    canManageInventory: z.boolean().default(false),
    canManageDebts: z.boolean().default(true),
    canViewStats: z.boolean().default(false),
    canApplyDiscount: z.boolean().default(false),
    canRefund: z.boolean().default(false),
    canCloseSession: z.boolean().default(false),
})
    .partial();
// ─── Login ────────────────────────────────────────────────────────
// Faza 26 P0-5: asosiy login identifikatori — telefon (+998 XX XXX XX XX).
// Backwards-compat: username bilan ham login ishlaydi (production'dagi mavjud
// foydalanuvchilar buzilmasligi uchun). Keyingi sprintda username deprecate
// qilinishi mumkin.
export const loginSchema = z
    .object({
    phone: phoneSchema.optional(),
    // Login format tekshirmaydi (loginUsernameSchema) — kiril/probel/do'kon
    // admini username'lari ham kira olsin. Format faqat YARATISHDA (usernameSchema).
    username: loginUsernameSchema.optional(),
    password: loginPasswordSchema,
})
    .refine((val) => val.phone !== undefined || val.username !== undefined, {
    message: 'Telefon yoki username kiritilishi shart',
    path: ['phone'],
});
// ─── Create user (yangi xodim qo'shish) ───────────────────────────
export const createUserSchema = z.object({
    username: usernameSchema,
    password: passwordSchema,
    name: z.string().min(2, 'Ism kamida 2 ta belgi').max(100),
    surname: z.string().max(100).optional(),
    phone: phoneSchema.optional(),
    email: emailSchema.optional(),
    role: userRoleEnum.default('XODIM'),
    subscriptionType: subscriptionTypeEnum.default('TRIAL'),
    subscriptionEnd: z.string().datetime().optional(),
    storeId: uuidSchema.optional(),
    permissions: userPermissionsSchema.optional(),
});
// ─── Update user (barcha fieldlar optional, parol alohida) ────────
export const updateUserSchema = createUserSchema.partial().omit({ password: true });
// ─── Change password ──────────────────────────────────────────────
export const changePasswordSchema = z
    .object({
    currentPassword: loginPasswordSchema,
    newPassword: passwordSchema,
    confirmPassword: z.string().min(1),
})
    .refine((data) => data.newPassword === data.confirmPassword, {
    message: 'Yangi parol va tasdiqlash mos kelmaydi',
    path: ['confirmPassword'],
});
// ─── Reset password (faqat EGASI uchun) ───────────────────────────
export const resetPasswordSchema = z.object({
    userId: uuidSchema,
    newPassword: passwordSchema,
});
// ─── Query (ro'yxat olish) ────────────────────────────────────────
export const userQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    limit: z.coerce.number().int().min(1).max(200).default(50),
    search: z.string().max(100).optional(),
    role: userRoleEnum.optional(),
    storeId: uuidSchema.optional(),
    isActive: z.coerce.boolean().optional(),
});
// ─── First owner (birinchi EGASI yaratish — fresh DB) ─────────────
export const createFirstOwnerSchema = z.object({
    username: usernameSchema,
    password: passwordSchema,
    name: z.string().min(2).max(100),
    phone: phoneSchema.optional(),
});
//# sourceMappingURL=user.schema.js.map